(() => {
  'use strict';
  if (window.rmCommerceReady) return;
  window.rmCommerceReady = true;
  let cartSubmissionState = 'idle';
  const root = () => window.Shopify?.routes?.root || '/';
  const cartFailureMessage = (status) => {
    if (status === 422) return 'No pudimos agregar toda la selecci\u00f3n. Revisa las cantidades y la disponibilidad.';
    if (status === 429) return 'La tienda est\u00e1 recibiendo muchas solicitudes. Espera un momento.';
    return 'No pudimos confirmar el \u00faltimo intento.';
  };
  const confirmsSelection = (result, items) => {
    if (!result || !Array.isArray(result.items) || result.items.length === 0) return false;
    return items.every(item => result.items.some(line => line &&
      String(line.variant_id ?? line.id) === String(item.id) &&
      Number.isSafeInteger(line.quantity) && line.quantity > 0));
  };
  const selectItems = (form) => {
    const totals = new Map();
    for (const input of form.querySelectorAll('[data-rm-quantity]')) {
      if (input.disabled) continue;
      const quantity = Number(input.value);
      if (!Number.isSafeInteger(quantity) || quantity < 0) throw new Error('Revisa las cantidades: usa n\u00fameros enteros a partir de cero.');
      if (quantity === 0) continue;
      const id = input.dataset.variantId;
      if (!/^[1-9]\d*$/.test(id || '')) throw new Error('No pudimos reconocer una de tus opciones. Vuelve a elegirla desde su p\u00e1gina de producto.');
      const next = (totals.get(id) || 0) + quantity;
      if (!Number.isSafeInteger(next)) throw new Error('Revisa la cantidad seleccionada.');
      totals.set(id, next);
    }
    return Array.from(totals, ([id, quantity]) => ({ id, quantity }));
  };
  const syncVariant = (form) => {
    const select = form.querySelector('[data-rm-variant]');
    const option = select?.selectedOptions[0];
    if (!option) return;
    const scope = form.closest('[data-rm-product]');
    const price = scope?.querySelector('[data-rm-price]');
    if (price) price.textContent = option.dataset.price || '';
    const available = option.dataset.available === 'true';
    const button = form.querySelector('[data-rm-submit]');
    if (button && form.dataset.locked !== 'true') {
      button.disabled = !available;
      button.textContent = available ? 'Agregar al carrito' : 'Agotado';
    }
    const image = scope?.querySelector('[data-rm-main-image] img');
    if (image && option.dataset.image) {
      image.src = option.dataset.image;
      image.removeAttribute('srcset');
      image.alt = option.dataset.imageAlt || scope.dataset.productTitle || '';
    }
    const url = new URL(window.location.href);
    url.searchParams.set('variant', option.value);
    window.history.replaceState({}, '', url);
  };
  const updateSummary = (form) => {
    const output = form.querySelector('[data-rm-selection-count]');
    if (!output) return;
    try {
      const count = selectItems(form).reduce((sum, item) => sum + item.quantity, 0);
      output.textContent = count === 1 ? '1 pieza seleccionada' : `${count} piezas seleccionadas`;
    } catch (error) { output.textContent = error.message; }
  };
  async function submitCart(form, bulk) {
    if (form.dataset.locked === 'true') return;
    const status = form.querySelector('[data-rm-status]');
    const button = form.querySelector('[data-rm-submit]');
    if (cartSubmissionState !== 'idle') {
      if (status) status.textContent = cartSubmissionState === 'pending'
        ? 'Estamos agregando tu selecci\u00f3n. Espera un momento.'
        : 'Revisa tu carrito antes de agregar m\u00e1s productos para evitar duplicados.';
      return;
    }
    if (!form.reportValidity()) return;
    let items;
    try {
      if (bulk) items = selectItems(form);
      else {
        const id = new FormData(form).get('id');
        const quantity = Number(new FormData(form).get('quantity'));
        if (!/^[1-9]\d*$/.test(String(id || '')) || !Number.isSafeInteger(quantity) || quantity < 1) throw new Error('Selecciona un dise\u00f1o y una cantidad v\u00e1lida.');
        items = [{ id: String(id), quantity }];
      }
      if (!items.length) throw new Error('Elige al menos una pieza para agregar.');
    } catch (error) { if (status) status.textContent = error.message; return; }
    cartSubmissionState = 'pending';
    form.dataset.locked = 'true';
    form.setAttribute('aria-busy', 'true');
    if (button) { button.disabled = true; button.textContent = 'Agregando\u2026'; }
    if (status) status.textContent = '';
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(`${root()}cart/add.js`, {
        method: 'POST', credentials: 'same-origin', signal: controller.signal,
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items })
      });
      if (!response.ok) {
        const error = new Error(cartFailureMessage(response.status));
        error.rmPublic = true;
        throw error;
      }
      const result = await response.json();
      if (!confirmsSelection(result, items)) throw new Error('Unconfirmed cart response');
      if (bulk) {
        form.querySelectorAll('[data-rm-quantity]').forEach(input => { input.value = '0'; });
        updateSummary(form);
      }
      cartSubmissionState = 'review';
      if (status) status.textContent = 'Selecci\u00f3n agregada. Abriendo el carrito\u2026';
      window.location.assign(`${root()}cart`);
    } catch (error) {
      cartSubmissionState = 'review';
      const message = error.name === 'AbortError' ? 'La operaci\u00f3n tard\u00f3 demasiado.' : (error.rmPublic ? error.message : 'No pudimos confirmar el \u00faltimo intento.');
      if (status) status.textContent = `${message} Revisa tu carrito antes de intentarlo de nuevo para evitar duplicados.`;
      if (button) button.textContent = 'Revisa el carrito';
    } finally { clearTimeout(timer); form.removeAttribute('aria-busy'); }
  }
  async function addCartBuilderItem(button) {
    if (cartSubmissionState !== 'idle') return;
    const id = String(button.dataset.variantId || '');
    if (!/^[1-9]\d*$/.test(id)) return;
    const builder = button.closest('[data-rm-cart-builder]');
    const status = builder?.querySelector('[data-rm-cart-add-status]');
    const buttons = Array.from(builder?.querySelectorAll('[data-rm-cart-add]') || []);
    const original = button.textContent;
    cartSubmissionState = 'pending';
    builder?.setAttribute('aria-busy','true');
    buttons.forEach(b => { b.disabled = true; });
    button.setAttribute('aria-label','Agregando al carrito');
    if (status) status.textContent = '';
    try {
      const response = await fetch(`${root()}cart/add.js`, {
        method:'POST',
        credentials:'same-origin',
        headers:{'Content-Type':'application/json','Accept':'application/json'},
        body:JSON.stringify({items:[{id,quantity:1}]})
      });
      if (!response.ok) {
        const error = new Error(cartFailureMessage(response.status));
        error.rmPublic = true;
        throw error;
      }
      const result = await response.json();
      if (!confirmsSelection(result,[{id,quantity:1}])) throw new Error('Unconfirmed cart response');
      if (status) status.textContent = 'Agregado. Actualizando tu carrito…';
      window.location.reload();
    } catch (error) {
      cartSubmissionState = 'idle';
      builder?.removeAttribute('aria-busy');
      buttons.forEach(b => { b.disabled = false; });
      button.textContent = original;
      button.removeAttribute('aria-label');
      if (status) status.textContent = error.rmPublic ? error.message : 'No pudimos confirmar el último intento. Revisa tu carrito antes de volver a intentarlo.';
    }
  }
  function init(scope = document) {
    scope.querySelectorAll('[data-rm-product-form]').forEach(form => {
      if (form.dataset.rmReady) return;
      form.dataset.rmReady = 'true';
      form.addEventListener('submit', event => { event.preventDefault(); submitCart(form, false); });
      form.querySelector('[data-rm-variant]')?.addEventListener('change', () => syncVariant(form));
      syncVariant(form);
    });
    scope.querySelectorAll('[data-rm-bulk-form]').forEach(form => {
      if (form.dataset.rmReady) return;
      form.dataset.rmReady = 'true'; form.hidden = false;
      form.addEventListener('input', () => updateSummary(form));
      form.addEventListener('submit', event => { event.preventDefault(); submitCart(form, true); });
      updateSummary(form);
    });
    scope.querySelectorAll('[data-rm-cart-add]').forEach(button => {
      if (button.dataset.rmCartAddReady) return;
      button.dataset.rmCartAddReady = 'true';
      button.addEventListener('click', () => addCartBuilderItem(button));
    });
    scope.querySelectorAll('[data-rm-cart-form]').forEach(form => {
      if (form.dataset.rmReady) return;
      form.dataset.rmReady = 'true';
      form.addEventListener('input', event => {
        if (event.target.name !== 'updates[]') return;
        const checkout = form.querySelector('[data-rm-checkout]');
        if (checkout) checkout.disabled = true;
        const status = form.querySelector('[data-rm-cart-status]');
        if (status) status.textContent = 'Actualiza el carrito para ver las cantidades y descuentos correctos antes de continuar.';
      });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    if (document.querySelector('[data-rm-cart-form]')) { window.location.reload(); return; }
    cartSubmissionState = 'idle';
    document.querySelectorAll('[data-rm-product-form], [data-rm-bulk-form]').forEach(form => {
      delete form.dataset.locked; form.removeAttribute('aria-busy');
      const status = form.querySelector('[data-rm-status]');
      if (status) status.textContent = '';
      if (form.hasAttribute('data-rm-product-form')) syncVariant(form);
      else {
        const button = form.querySelector('[data-rm-submit]');
        if (button) { button.disabled = false; button.textContent = 'Agregar mi selecci\u00f3n'; }
        updateSummary(form);
      }
    });
  });
})();
